/**
 * cart-booking-validator.js
 *
 * Prevents stale installation bookings from being purchased.
 *
 * Problem: Shopify carts persist indefinitely. A customer can add a product
 * with an installation date (stored as line item property "paiva"), abandon
 * the cart, and return days or weeks later — completing the purchase with an
 * outdated booking. This causes double-bookings in Tehden.
 *
 * A concrete case: a customer applied for Svea financing, was redirected back
 * to the store without completing checkout (two-generals problem — no
 * guaranteed end state), and assumed the order had gone through. The cart
 * remained alive with the original booking date.
 *
 * Solution: on every page load, fingerprint the booking items in the cart.
 * If the fingerprint has not changed for more than MAX_AGE_MS (24 h), clear
 * the entire cart and notify the customer. Any modification to the cart
 * (new item, quantity change, different date) resets the clock.
 *
 * Additionally, the booking date ("paiva" property) is validated on every load.
 * If the date is in the past, today, or fewer than MIN_DAYS_AHEAD days from now,
 * the cart is cleared immediately with its own notification.
 *
 * Storage:
 *   Cookie "cart_booking_ts" — JSON { signature: string, timestamp: number }
 *   sessionStorage "cart-booking-expired"      — flag for 24 h expiry, read once after reload
 *   sessionStorage "cart-booking-date-expired" — flag for stale date, read once after reload
 *
 * Testing (browser console):
 *   const c = JSON.parse(decodeURIComponent(document.cookie.match(/cart_booking_ts=([^;]*)/)[1]));
 *   c.timestamp = Date.now() - (25 * 60 * 60 * 1000);
 *   document.cookie = 'cart_booking_ts=' + encodeURIComponent(JSON.stringify(c)) + ';path=/;max-age=604800;samesite=lax';
 *   location.reload();
 */

(function () {
  const COOKIE_NAME = 'cart_booking_ts';
  const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
  const MIN_DAYS_AHEAD = 2; // booking date must be at least this many days from now
  const DEBUG = false; // set true to enable console logging

  const STOREFRONT_TOKEN = 'c67e06f479969086ae1fd479cad4cbc1';
  const STOREFRONT_URL = '/api/2023-07/graphql.json';
  // Mirrors the slot list in booking-form.liquid (minutes from midnight).
  const ALL_SLOTS = [
    { label: '08:00', start: 8 * 60,  end: 10 * 60 },
    { label: '10:00', start: 10 * 60, end: 12 * 60 },
    { label: '12:00', start: 12 * 60, end: 14 * 60 },
  ];

  /**
   * Returns the parsed value of a cookie, or null if missing / unparseable.
   * @param {string} name
   * @returns {{ signature: string, timestamp: number } | null}
   */
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  /**
   * Writes a JSON-serialisable value as a cookie.
   * Max-age is set to 7 days — well beyond MAX_AGE_MS so the cookie is never
   * the limiting factor.
   * @param {string} name
   * @param {{ signature: string, timestamp: number }} value
   */
  function setCookie(name, value) {
    document.cookie =
      name + '=' + encodeURIComponent(JSON.stringify(value)) +
      ';path=/;max-age=' + (7 * 24 * 3600) + ';samesite=lax';
  }

  /**
   * Deletes a cookie by setting max-age to 0.
   * @param {string} name
   */
  function deleteCookie(name) {
    document.cookie = name + '=;path=/;max-age=0';
  }

  /**
   * Parses a booking date string. Supports "DD.MM.YYYY" (Finnish) and ISO "YYYY-MM-DD".
   * Returns a Date at midnight local time, or null if unparseable.
   * @param {string} dateStr
   * @returns {Date|null}
   */
  function parseBookingDate(dateStr) {
    if (!dateStr) return null;
    const fi = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (fi) return new Date(Number(fi[3]), Number(fi[2]) - 1, Number(fi[1]));
    const ts = Date.parse(dateStr);
    return isNaN(ts) ? null : new Date(ts);
  }

  /**
   * Returns true if any booking item's date is in the past or within MIN_DAYS_AHEAD days.
   * @param {Array<object>} items
   * @returns {boolean}
   */
  function hasStaleBookingDate(items) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + MIN_DAYS_AHEAD);
    return items.some((item) => {
      if (!item.properties || !item.properties.paiva) return false;
      const d = parseBookingDate(item.properties.paiva);
      return d !== null && d < minDate;
    });
  }

  /**
   * Fetches all ajanvaraus metaobjects for a given toimipiste via Storefront API.
   * @param {string} location
   * @returns {Promise<Array<{paiva:string, aika:string, loppu_aika:string, toimipiste:string}>>}
   */
  async function fetchBookingsForLocation(location) {
    let hasNextPage = true;
    let after = null;
    const all = [];
    try {
      while (hasNextPage) {
        const query = `{ metaobjects(type:"ajanvaraus",first:250${after ? `,after:"${after}"` : ''}) {
          edges { cursor node { fields { key value } } }
          pageInfo { hasNextPage }
        } }`;
        const res = await fetch(STOREFRONT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN },
          body: JSON.stringify({ query }),
        });
        if (!res.ok) return [];
        const result = await res.json();
        if (result.errors) return [];
        const edges = result.data.metaobjects.edges;
        edges.forEach(edge => {
          const f = edge.node.fields;
          const get = key => (f.find(x => x.key === key) || {}).value;
          all.push({ paiva: get('paiva'), aika: get('aika'), loppu_aika: get('loppu_aika'), toimipiste: get('toimipiste') });
        });
        hasNextPage = result.data.metaobjects.pageInfo.hasNextPage;
        after = edges.length ? edges[edges.length - 1].cursor : null;
      }
    } catch (e) {
      return [];
    }
    const filtered = all.filter(b => b.toimipiste === location);
    if (DEBUG) console.log('[cart-booking-validator] bookings for "' + location + '":', JSON.parse(JSON.stringify(filtered)));
    return filtered;
  }

  /**
   * Fetches nostinten_maara for a toimipiste from the toimipisteet metaobjects.
   * Falls back to 1 on any error.
   * @param {string} location
   * @returns {Promise<number>}
   */
  async function fetchBookingLimit(location) {
    try {
      const query = `{ metaobjects(type:"toimipisteet",first:50) { edges { node { fields { key value } } } } }`;
      const res = await fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) return 1;
      const result = await res.json();
      if (result.errors) return 1;
      for (const edge of result.data.metaobjects.edges) {
        const f = edge.node.fields;
        const get = key => (f.find(x => x.key === key) || {}).value;
        if (get('nimi') === location) return parseInt(get('nostinten_maara'), 10) || 1;
      }
    } catch (e) { /* fall through */ }
    return 1;
  }

  /**
   * Returns true if the booked slot for any cart item is no longer available.
   * Uses the same overlap logic as booking-form.liquid.
   * @param {Array<object>} items
   * @returns {Promise<boolean>}
   */
  async function hasUnavailableSlot(items) {
    const bookingItems = items.filter(
      item => item.properties && item.properties.paiva && item.properties.aika && item.properties.toimipiste
    );
    if (!bookingItems.length) return false;

    // To test a full slot, uncomment and set DEBUG=true:
    // window.__TEST_BOOKINGS = [
    //   { paiva: '2026-05-13', aika: '10:00', loppu_aika: '12:00', toimipiste: 'Kaarina' },
    //   { paiva: '2026-05-13', aika: '10:00', loppu_aika: '12:00', toimipiste: 'Kaarina' },
    //   { paiva: '2026-05-13', aika: '10:00', loppu_aika: '12:00', toimipiste: 'Kaarina' },
    //   { paiva: '2026-05-13', aika: '10:00', loppu_aika: '12:00', toimipiste: 'Kaarina' },
    //   { paiva: '2026-05-13', aika: '10:00', loppu_aika: '12:00', toimipiste: 'Kaarina' },
    // ];
    const testBookings = Array.isArray(window.__TEST_BOOKINGS) ? window.__TEST_BOOKINGS : [];
    if (DEBUG && testBookings.length) {
      console.log('[cart-booking-validator] __TEST_BOOKINGS injected:', testBookings);
    }

    const byLocation = {};
    bookingItems.forEach(item => {
      const loc = item.properties.toimipiste;
      (byLocation[loc] = byLocation[loc] || []).push(item);
    });

    function toMin(str) {
      if (!str) return null;
      const [h, m] = str.split(':').map(Number);
      return h * 60 + m;
    }

    for (const [location, locationItems] of Object.entries(byLocation)) {
      const [fetched, limit] = await Promise.all([
        fetchBookingsForLocation(location),
        fetchBookingLimit(location),
      ]);
      const bookings = fetched.concat(testBookings.filter(b => b.toimipiste === location));
      if (DEBUG) console.log('[cart-booking-validator] checking location "' + location + '", limit=' + limit + ', total bookings used:', bookings);
      for (const item of locationItems) {
        const slot = ALL_SLOTS.find(s => s.label === item.properties.aika);
        if (!slot) continue;
        let count = 0;
        bookings.forEach(b => {
          if (b.paiva !== item.properties.paiva || !b.aika) return;
          const bStart = toMin(b.aika);
          const bEnd = b.loppu_aika ? toMin(b.loppu_aika) : bStart + 120;
          if (slot.start < bEnd && slot.end > bStart) count++;
        });
        if (DEBUG) console.log('[cart-booking-validator] slot', item.properties.aika, 'on', item.properties.paiva, '→ count=' + count + ' limit=' + limit + (count >= limit ? ' → FULL' : ' → ok'));
        if (count >= limit) return true;
      }
    }
    return false;
  }

  /**
   * Builds a stable fingerprint of all booking items in the cart.
   * Includes variant ID, quantity, and the booked date ("paiva" property).
   * Returns an empty string when no booking items are present.
   *
   * The fingerprint changes whenever the customer modifies the cart, which
   * resets the 24 h expiry clock.
   *
   * @param {Array<object>} items - Shopify cart line items from /cart.js
   * @returns {string}
   */
  function getBookingSignature(items) {
    return items
      .filter((item) => item.properties && item.properties.paiva)
      .map((item) => item.variant_id + ':' + item.quantity + ':' + item.properties.paiva)
      .join('|');
  }

  /**
   * Main validation routine. Runs on every page load.
   *
   * Flow:
   *   1. Fetch current cart from Shopify.
   *   2. Compute booking fingerprint.
   *   3a. No booking items → delete tracking cookie, exit.
   *   3b. Fingerprint differs from stored → cart was modified, reset clock, exit.
   *   3c. Fingerprint matches and age < 24 h → still fresh, exit.
   *   3d. Fingerprint matches and age ≥ 24 h → clear cart, set notification flag, reload.
   */
  async function validateCartBooking() {
    let cart;
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
    } catch (e) {
      return;
    }

    // Booking date in the past or too soon → clear immediately.
    if (hasStaleBookingDate(cart.items || [])) {
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        deleteCookie(COOKIE_NAME);
        sessionStorage.setItem('cart-booking-date-expired', '1');
        window.location.reload();
      } catch (e) {
        console.error('Cart booking clear failed:', e);
      }
      return;
    }

    // Booked slot no longer available → clear immediately.
    if (await hasUnavailableSlot(cart.items || [])) {
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        deleteCookie(COOKIE_NAME);
        sessionStorage.setItem('cart-booking-slot-taken', '1');
        window.location.reload();
      } catch (e) {
        console.error('Cart booking clear failed:', e);
      }
      return;
    }

    const signature = getBookingSignature(cart.items || []);

    if (!signature) {
      deleteCookie(COOKIE_NAME);
      return;
    }

    const stored = getCookie(COOKIE_NAME);
    const now = Date.now();

    if (!stored || stored.signature !== signature) {
      // Cart was modified — reset the expiry clock.
      setCookie(COOKIE_NAME, { signature, timestamp: now });
      return;
    }

    if (now - stored.timestamp < MAX_AGE_MS) {
      return;
    }

    // Booking unchanged for 24 h → clear the whole cart.
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      deleteCookie(COOKIE_NAME);
      sessionStorage.setItem('cart-booking-expired', '1');
      window.location.reload();
    } catch (e) {
      console.error('Cart booking clear failed:', e);
    }
  }

  /**
   * Shows a dismissable banner if the cart was just cleared due to expiry.
   * Reads a one-time flag from sessionStorage set before the reload.
   * Supports Finnish (default), Swedish, and English.
   */
  function maybeShowExpiredBanner() {
    const ageExpired = sessionStorage.getItem('cart-booking-expired');
    const dateExpired = sessionStorage.getItem('cart-booking-date-expired');
    const slotTaken = sessionStorage.getItem('cart-booking-slot-taken');
    if (!ageExpired && !dateExpired && !slotTaken) return;
    sessionStorage.removeItem('cart-booking-expired');
    sessionStorage.removeItem('cart-booking-date-expired');
    sessionStorage.removeItem('cart-booking-slot-taken');

    const lang = document.documentElement.lang || '';
    let message;
    if (slotTaken) {
      if (lang.startsWith('sv')) {
        message = 'Din kundvagn har tömts eftersom den valda installtationstiden inte längre är tillgänglig. Välj ett nytt installationsdatum.';
      } else if (lang.startsWith('en')) {
        message = 'Your cart has been cleared because the selected installation time is no longer available. Please select a new installation date.';
      } else {
        message = 'Ostoskorisi on tyhjennetty, koska valittu asennusaika ei ole enää saatavilla. Ole hyvä ja valitse uusi asennusaika.';
      }
    } else if (dateExpired) {
      if (lang.startsWith('sv')) {
        message = 'Din kundvagn har tömts eftersom bokningsdatumet är passerat eller för nära. Välj ett nytt installationsdatum.';
      } else if (lang.startsWith('en')) {
        message = 'Your cart has been cleared because the booking date is in the past or too soon. Please select a new installation date.';
      } else {
        message = 'Ostoskorisi on tyhjennetty, koska varattu asennuspäivä on menneisyydessä tai liian lähellä. Ole hyvä ja valitse uusi asennusaika.';
      }
    } else if (lang.startsWith('sv')) {
      message = 'Din kundvagn har tömts eftersom du inte slutförde köpet inom 24 timmar. Välj ett nytt installationsdatum.';
    } else if (lang.startsWith('en')) {
      message = 'Your cart has been cleared because the purchase was not completed within 24 hours. Please select a new installation date.';
    } else {
      message = 'Ostoskorisi on tyhjennetty, koska ostosta ei viety loppuun 24 tunnin sisällä. Ole hyvä ja valitse uusi asennusaika.';
    }

    const banner = document.createElement('div');
    banner.setAttribute('role', 'alert');
    banner.style.cssText =
      'position:fixed;top:0;left:0;right:0;background:#c8250e;color:#fff;' +
      'padding:1.2rem 2rem;text-align:center;z-index:99999;font-size:1.5rem;line-height:1.4;';
    banner.textContent = message;

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.setAttribute('aria-label', 'Sulje');
    closeBtn.style.cssText =
      'margin-left:1.5rem;background:none;border:none;color:#fff;cursor:pointer;font-size:1.5rem;vertical-align:middle;';
    closeBtn.addEventListener('click', () => banner.remove());
    banner.appendChild(closeBtn);

    document.body.prepend(banner);
    setTimeout(() => banner.remove(), 12000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      maybeShowExpiredBanner();
      validateCartBooking();
    });
  } else {
    maybeShowExpiredBanner();
    validateCartBooking();
  }
})();
